import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { useEffect, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import { DatePicker, FormLayout, FormRow, FormSection, Select, TextInput } from '~/components';
import { useCustomForm } from '~/hooks/useCustomForm';
import { b2bSideCopy } from '../../../../b2bSideCopy';
import { startDateFloor } from '../../../shared-steps/startDate';
import { selectWizardListingSide } from '../../listingSide';
import { useMsaasListingsActorRef } from '../../msaasProvider';
import type { Context as MsaasListingContext } from '../../types';
import { emptyLaborMetricsForm, LABOR_REQ_TYPE_OPTIONS } from './constants';
import { createLaborMetricsFormSchema, LaborMetricsFormValues } from './types';
import { transformApiToLaborMetricsForm } from './utils';

const TAGS = CT.employer.msaasListings.laborMetricsForm;

export const LaborMetricsForm = ({ footer }: { footer: React.ReactNode }) => {
  const msaasListingsRef = useMsaasListingsActorRef();
  const context = useSelector(msaasListingsRef, (s): MsaasListingContext => {
    if (typeof s === 'object' && s !== null && 'context' in s) {
      return s.context as MsaasListingContext;
    }
    throw new Error('Invalid state: context not found');
  });
  const { laborMetricsRef, listing: msaasListing } = context;
  // [MCV-1889] A selling listing offers labor, so the labels say available rather than needed.
  const copy = b2bSideCopy(selectWizardListingSide(context));

  // [MCV-1524] The start date already on the record is allowed to stay in the
  // past; only *moving* it into the past is rejected. Memoised so the resolver
  // identity is stable across renders.
  const persistedStartDate = msaasListing?.detail?.startDate;
  const schema = useMemo(
    () => createLaborMetricsFormSchema(persistedStartDate),
    [persistedStartDate]
  );

  const methods = useCustomForm<LaborMetricsFormValues>({
    //@ts-expect-error typing for preprocess parsing does not work well
    resolver: zodResolver(schema),
    defaultValues: msaasListing?.detail?.id
      ? transformApiToLaborMetricsForm(msaasListing)
      : emptyLaborMetricsForm,
  });

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { isDirty },
  } = methods;

  useEffect(() => {
    if (msaasListing) {
      setValue('msaasListingId', msaasListing.id);
    }
  }, [msaasListing]);

  const onSubmitSuccess = (values: LaborMetricsFormValues) => {
    const existingDetail = msaasListing?.detail?.id;
    if (existingDetail && isDirty) {
      laborMetricsRef.send({ type: 'ON_UPDATE', payload: { values } });
    } else if (!existingDetail) {
      laborMetricsRef.send({ type: 'ON_ADD', payload: { values } });
    }
    msaasListingsRef.send({ type: 'ON_NEXT' });
  };

  const onSubmitError = (err: unknown) => {
    console.log('Labor Metrics Info Form Submit Error: ', err, methods.getValues());
  };

  const numOfFtes = watch('numOfFtes') || 0;
  const fteShiftsPerYear = watch('fteShiftsPerYear');
  const hoursPerShift = watch('hoursPerShift');
  const laborReqType = watch('laborReqType').value;
  const minRvusPerShift = watch('minRvusPerShift') || 0;
  const numRvusReq = watch('numRvusReq') || 0;
  const totalShifts =
    laborReqType === 'FTE'
      ? numOfFtes * fteShiftsPerYear
      : minRvusPerShift
        ? Math.ceil(numRvusReq / minRvusPerShift)
        : null;

  useEffect(() => {
    if (laborReqType === 'RVU') {
      setValue('numOfFtes', null);
    } else {
      setValue('numRvusReq', null);
    }
  }, [laborReqType]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}>
        <FormLayout>
          <h3 className="sub-heading-sb">Contract Duration</h3>
          <FormSection componentsPerLine={1}>
            <FormRow>
              <Select
                name={'laborReqType'}
                cyTag={TAGS.laborReqType}
                label={'Labor Requirements Type'}
                options={LABOR_REQ_TYPE_OPTIONS}
              />
            </FormRow>
          </FormSection>
          <FormSection componentsPerLine={2}>
            <FormRow>
              <DatePicker
                name={'startDate'}
                cyTag={TAGS.startDate}
                label={'Start Date'}
                // [MCV-1524] Floor the picker at today unless the record
                // already carries an earlier date, which must stay selectable
                // so an in-flight listing can still be edited.
                minDate={startDateFloor(persistedStartDate)}
                required
              />
            </FormRow>
            <FormRow>
              <DatePicker name={'endDate'} cyTag={TAGS.endDate} label={'End Date'} />
            </FormRow>
          </FormSection>
          <h3 className="sub-heading-sb">{copy.laborHeading}</h3>
          <FormSection componentsPerLine={3}>
            <FormRow>
              {laborReqType === 'FTE' ? (
                <TextInput
                  name={'numOfFtes'}
                  cyTag={TAGS.numOfFtes}
                  label={'Number of Providers (FTEs)'}
                  description={copy.fteDescription}
                  type="number"
                  // [MCV-1541] Marked required through the label component rather
                  // than a literal '*' in the string, so the field carries the
                  // screen-reader marker the RVU fields already had — and now
                  // actually blocks the step (see ./types.ts).
                  required
                />
              ) : (
                <TextInput
                  name={'numRvusReq'}
                  cyTag={TAGS.numRvusReq}
                  label={copy.totalRvusLabel}
                  description="Relative Value Units needed"
                  type="number"
                  required={laborReqType === 'RVU'}
                />
              )}
            </FormRow>
            <FormRow>
              <TextInput
                name={'fteShiftsPerYear'}
                cyTag={TAGS.fteShiftsPerYear}
                label={'Shifts per Provider per Year'}
                description="Default: 200 shifts/year"
                type="number"
                required
              />
            </FormRow>
            <FormRow>
              <TextInput
                name={'hoursPerShift'}
                cyTag={TAGS.hoursPerShift}
                label={'Hours per Shift'}
                description="Default: 8 hours"
                type="number"
                required
              />
            </FormRow>
            <FormRow>
              <TextInput
                name={'minRvusPerShift'}
                cyTag={TAGS.minRvusPerShift}
                label={'Minimum RVUs per Shift'}
                type="number"
                required={laborReqType === 'RVU'}
                description={
                  laborReqType === 'RVU' ? undefined : 'Optional - enables $/RVU comparison'
                }
              />
            </FormRow>
          </FormSection>
          <div className="w-full bg-primary-t-900 p-4 rounded-lg">
            <p className="body-text-sb mb-3">Calculated Totals: </p>
            <ul className="body-text list-disc list-inside">
              <li className="mb-2" data-cy={TAGS.totalShifts}>
                Total Shifts:{' '}
                {laborReqType === 'FTE' ? (
                  <>
                    <b className="font-bold body-text-sb">{totalShifts}</b> ({numOfFtes} FTEs *{' '}
                    {fteShiftsPerYear} shifts):
                  </>
                ) : (
                  <>
                    {numRvusReq && minRvusPerShift ? (
                      <>
                        <b className="font-bold body-text-sb">{totalShifts}</b> ({numRvusReq} RVUs
                        Required / {minRvusPerShift} minimum RVUs per shift)
                      </>
                    ) : (
                      <b className="font-bold body-text-sb">--</b>
                    )}
                  </>
                )}
              </li>
              {laborReqType === 'RVU' && (
                <li className="mb-2" data-cy={TAGS.totalFTEs}>
                  {copy.ftesTotalLabel}
                  {totalShifts && fteShiftsPerYear && minRvusPerShift ? (
                    <>
                      <b className="font-bold body-text-sb">
                        {Math.ceil(totalShifts / fteShiftsPerYear)}{' '}
                      </b>
                      ({totalShifts} total shifts / {fteShiftsPerYear} shifts per FTE)
                    </>
                  ) : (
                    <b className="font-bold body-text-sb">--</b>
                  )}
                </li>
              )}
              <li className="mb-2" data-cy={TAGS.totalHours}>
                Total Hours:
                {totalShifts && hoursPerShift ? (
                  <>
                    <b className="font-bold body-text-sb">{totalShifts * hoursPerShift} </b>(
                    {totalShifts} shifts * {hoursPerShift} hours)
                  </>
                ) : (
                  <b className="font-bold body-text-sb">--</b>
                )}
              </li>
            </ul>
          </div>
          {footer}
        </FormLayout>
      </form>
    </FormProvider>
  );
};
